import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Stack,
  Container,
  Typography,
  TextField,
  Avatar,
  IconButton,
  Grid,
  Snackbar,
  Alert
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import Page from '../components/Page';
import GooglePlaces from 'src/components/GooglePlaces';
import { api } from 'src/Api';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';

export default function EditProfile() {
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem('user'));

  // Initialize states with userData
  const [firstName, setFirstName] = useState(userData.first_name || '');
  const [lastName, setLastName] = useState(userData.last_name || '');
  const [email] = useState(userData.email); // Disabled, so static
  const [role] = useState(userData.role.role_name); // Disabled, so static
  const [phoneNumber, setPhoneNumber] = useState(userData.contact_no || '');
  const [company, setCompany] = useState(userData?.company || '');
  const [file, setfile] = useState({ image: userData.avatar, file: '' });
  const [address, setaddress] = useState(null);

  const [loading, setloading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    if (userData && userData?.location) {
      setaddress({
        address: userData?.location,
        lat: userData?.lat || null,
        lon: userData?.lon || null,
        country: userData?.country || null,
        city: userData?.city || null
      });
    }
  }, []);

  const [objAfter, setobjAfter] = useState({
    name: userData?.name,
    file: false
  });

  // Handle form save
  const handleSave = () => {
    if (role == 'Broker' || role == 'Carrier') {
      if (!company) {
        setSnackbarOpen(true);
        setSnackbarMessage('Company name is required.');
        setSnackbarSeverity('error');
        return;
      }
    }

    const token = localStorage.getItem('token');
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formdata = new FormData();
    formdata.append('first_name', firstName);
    formdata.append('last_name', lastName);
    formdata.append('contact_no', phoneNumber);
    formdata.append('company', company);
    if (address) {
      formdata.append('location', address?.address);
      formdata.append('country', address?.country);
      formdata.append('city', address?.city);
      formdata.append('lat', address?.lat);
      formdata.append('lon', address?.lon);
    }

    if (file && file.file) {
      formdata.append('avatar', file?.file);
    }

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formdata,
      redirect: 'follow'
    };
    setloading(true);
    fetch(api.updateProfile, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const response = JSON.parse(body);
        if (status === 200 || status === 201) {
          localStorage.setItem('user', JSON.stringify(response?.data));
          setSnackbarOpen(true);
          setSnackbarMessage(response.message);
          setSnackbarSeverity('success');
          navigate('/dashboard/app');
        } else {
          setSnackbarOpen(true);
          setSnackbarMessage(response.error);
          setSnackbarSeverity('error');
        }
      })
      .catch((error) => console.log('error', error))
      .finally(() => setloading(false));
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Page title={`Edit Profile | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="flex-start" mb={2}>
          <IconButton
            style={{
              marginRight: 12,
              height: 40,
              width: 70,
              borderRadius: 5,
              backgroundColor: 'rgba(209,209,209,0.3)'
            }}
            onClick={() => navigate(-1)}
          >
            <Iconify icon="ion:return-up-back" style={{ fontSize: 25, color: '#010f58' }} />
          </IconButton>
          <Typography variant="h4" sx={{ color: '#010f58' }}>
            Edit Profile
          </Typography>
        </Stack>

        <Card sx={{ p: 3, pb: 2 }}>
          <Stack
            mb={2}
            direction={{ xs: 'column' }}
            style={{ display: 'flex', flexDirection: 'row' }}
            className="image_center"
          >
            <Box display="flex" justifyContent="center" mb={2} position="relative">
              {file && (
                <Avatar
                  alt={firstName}
                  src={objAfter?.file ? file.image : file.image}
                  sx={{ width: 120, height: 120 }}
                  className="image_center"
                />
              )}

              <IconButton
                color="primary"
                aria-label="upload picture"
                component="label"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 5,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  height: 30,
                  width: 30
                }}
              >
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={(e) => {
                    setobjAfter({
                      file: true
                    });
                    setfile({
                      image: URL.createObjectURL(e.target.files[0]),
                      file: e.target.files[0]
                    });
                  }}
                />
                <PhotoCamera sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={firstName}
                onChange={(e) => {
                  if (e.target.value.length <= 100) {
                    setFirstName(e.target.value); // Only update if length is <= 100
                  }
                }}
                className="custom-textfield"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={lastName}
                onChange={(e) => {
                  if (e.target.value.length <= 100) {
                    setLastName(e.target.value); // Only update if length is <= 100
                  }
                }}
                className="custom-textfield"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                value={email}
                disabled
                className="custom-textfield"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Role"
                value={role}
                disabled
                className="custom-textfield"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                // type='number'
                fullWidth
                label={role == 'Broker' || role == 'Carrier' ? 'Company*' : 'Company'}
                value={company}
                onChange={(e) => {
                  if (e.target.value.length <= 50) {
                    setCompany(e.target.value); // Only update if length is <= 100
                  }
                }}
                className="custom-textfield"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                type="number"
                fullWidth
                label="Contact"
                value={phoneNumber}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/[^0-9]/g, ''); // Only allow numeric characters
                  if (numericValue.length <= 20) {
                    setPhoneNumber(numericValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                    e.preventDefault(); // Prevent "e", "E", "+", and "-" from being typed
                  }
                }}
                className="custom-textfield"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <GooglePlaces
                width="100%"
                onResult={(val) => {
                  setaddress(val);
                }}
                placeholder={'Address'}
                // regions={true}
                address={address ? address?.address : userData?.location}
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <LoadingButton
              onClick={handleSave}
              color="primary"
              variant="contained"
              loading={loading}
              sx={{ fontSize: 12 }}
            >
              Save Changes
            </LoadingButton>
          </Box>
        </Card>
        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Page>
  );
}
